import { useDndContext } from "@dnd-kit/core";
import { faEllipsisV } from "@fortawesome/pro-solid-svg-icons";
import { Box, BoxProps, Button } from "@mui/material";
import {
  CSSProperties,
  forwardRef,
  MouseEvent,
  PropsWithChildren,
} from "react";

import { Icon } from "../../components/ui-v2";
import { useDrive } from "./DriveContext";
import { DriveItem as DriveItemModule, DriveViewType } from "./types";

type DriveItemProps = PropsWithChildren<
  BoxProps & {
    title?: string;
    type: DriveItemModule["__typename"];
    viewType: DriveViewType;
    isOver?: boolean;
    isSelected: boolean;
    isDragging: boolean;
    isSelectable: boolean;
    onClick: (event: MouseEvent) => void;
    onContextMenu: (event: MouseEvent) => void;
    draggableStyle?: CSSProperties;
  }
>;

type EllipsisButtonProps = {
  onClick: (event: MouseEvent) => void;
  isSelected: boolean;
};

export const EllipsisButton = ({
  onClick,
  isSelected,
}: EllipsisButtonProps) => {
  return (
    <Box
      className="ellipsis"
      sx={{
        position: "absolute",
        right: 0,
        top: 0,
        height: "48px",
        width: "32px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        transitionProperty: "opacity",
        transitionTimingFunction: "cubic-bezier(0.4, 0, 0.2, 1)",
        transitionDuration: "150ms",
        opacity: isSelected ? 1 : 0,
      }}
    >
      <Button
        onClick={onClick}
        variant="text"
        disableRipple
        color="secondary"
        sx={{
          height: "32px",
          width: "32px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "transparent",
          boxShadow: "none",

          "&:hover": {
            backgroundColor: "transparent",
          },
        }}
      >
        <Icon icon={faEllipsisV} />
      </Button>
    </Box>
  );
};

const DriveItem = forwardRef(function DriveItem(
  {
    title,
    type,
    viewType,
    isOver,
    isSelected,
    isDragging = false,
    isSelectable,
    onClick,
    onDoubleClick,
    onContextMenu,
    draggableStyle,
    children,
    ...boxProps
  }: DriveItemProps,
  ref,
) {
  const { actionsDisabled } = useDrive();
  const { active: isItemDragActive } = useDndContext();

  const handleTableClick = (event: MouseEvent) => {
    event.stopPropagation();
    if (isItemDragActive) {
      return;
    }

    if (onClick) {
      onClick(event);
    }
  };

  const handleGridClick = (event: MouseEvent) => {
    event.stopPropagation();
  };

  return (
    <Box
      title={title}
      ref={ref}
      onClick={viewType === "table" ? handleTableClick : handleGridClick}
      onDoubleClick={viewType === "table" ? onDoubleClick : undefined}
      sx={{
        position: "relative",
        width: "100%",
        display: "flex",
        flexDirection: viewType === "grid" ? "column" : "row",
        alignItems: "center",
        marginBottom: "4px",
        borderRadius: "8px",
        pointerEvents: isDragging ? "none" : "all",

        ...(viewType === "grid" && {
          "&:hover .ellipsis": {
            opacity: 1,
          },
        }),

        ...(viewType === "grid" &&
          isSelected && {
            ".ellipsis": {
              opacity: 1,
            },
          }),

        ...(viewType === "table" && {
          "&:hover": {
            cursor: "pointer",
          },
          "&:hover > .ellipsis": {
            opacity: 1,
          },
          "&:hover:before": {
            content: '""',
            position: "absolute",
            height: "100%",
            width: "100%",
            top: 0,
            left: 0,
            borderRadius: "8px",
            backgroundColor: "#2C2D3C",
            boxShadow: "inset 0 0 0 0.5px rgba(65, 66, 91, 0.50)",
            color: "#FAFAFF",
            zIndex: -1,
          },
        }),

        ...(viewType === "table" &&
          isSelected && {
            backgroundColor: "#2C2D3C",
            boxShadow:
              "inset 0 0 0 0.5px rgba(65, 66, 91, 0.75), 0px 1px 4px rgba(0, 0, 0, 0.05)",
            color: "#FAFAFF",
            "&:before": {
              content: '""',
              position: "absolute",
              height: "100%",
              width: "100%",
              top: 0,
              left: 0,
              borderRadius: "8px",
              backgroundColor: "#2C2D3C",
              boxShadow: "inset 0 0 0 0.5px rgba(65, 66, 91, 0.50)",
              color: "#FAFAFF",
              zIndex: -1,
            },
          }),
        ...(isOver &&
          viewType === "table" && {
            backgroundColor: "#2C2D3C",
            boxShadow: "inset 0 0 0 0.5px rgba(56,189,248,0.4)",
          }),
        ...(isItemDragActive &&
          type !== "Directory" && {
            opacity: 0.3,
          }),
        ...(isSelected &&
          isItemDragActive && {
            opacity: 0.3,
          }),
        ...(isSelectable === false &&
          type !== "Directory" && {
            pointerEvents: "none",
            opacity: 0.5,
          }),
      }}
      onContextMenu={actionsDisabled ? undefined : onContextMenu}
      style={draggableStyle}
      {...boxProps}
    >
      {children}
    </Box>
  );
});

export default DriveItem;
